import { z } from "zod";

const DEFAULT_PUBLIC_SITE_URL = "https://apex.xiaoshuiguai.cn";

const OptionalUrlSchema = z.preprocess(emptyStringToUndefined, z.url().optional());
const OptionalStringSchema = z.preprocess(emptyStringToUndefined, z.string().min(1).optional());

export const EnvSchema = z.object({
  LOG_LEVEL: z.preprocess(emptyStringToUndefined, z.string().min(3).max(10).default("trace")),
  PUBLIC_SITE_URL: OptionalUrlSchema,
  SENTRY_DSN: OptionalUrlSchema,
  SENTRY_ENVIRONMENT: OptionalStringSchema,
  SENTRY_RELEASE: OptionalStringSchema,
  UMAMI_URL: OptionalUrlSchema,
  UMAMI_WEBSITE_ID: OptionalStringSchema,
});

export const PublicEnvSchema = EnvSchema.pick({
  PUBLIC_SITE_URL: true,
  UMAMI_URL: true,
  UMAMI_WEBSITE_ID: true,
  SENTRY_DSN: true,
  SENTRY_ENVIRONMENT: true,
  SENTRY_RELEASE: true,
});

export type EnvSchema = z.output<typeof EnvSchema>;
export type PublicEnvSchema = z.output<typeof PublicEnvSchema>;

export function getEnv() {
  return EnvSchema.parse(process.env);
}

export function getPublicEnv() {
  const injected = readInjectedPublicEnv();
  if (injected) {
    return injected;
  }

  return PublicEnvSchema.parse({
    PUBLIC_SITE_URL: readEnvValue("PUBLIC_SITE_URL"),
    UMAMI_URL: readEnvValue("UMAMI_URL"),
    UMAMI_WEBSITE_ID: readEnvValue("UMAMI_WEBSITE_ID"),
    SENTRY_DSN: readEnvValue("SENTRY_DSN"),
    SENTRY_ENVIRONMENT: readEnvValue("SENTRY_ENVIRONMENT"),
    SENTRY_RELEASE: readEnvValue("SENTRY_RELEASE"),
  });
}

export function getPublicSiteUrl() {
  return normalizePublicSiteUrl(getPublicEnv().PUBLIC_SITE_URL ?? DEFAULT_PUBLIC_SITE_URL);
}

function readInjectedPublicEnv() {
  if (typeof window === "undefined") {
    return null;
  }

  const injected = window.__INJECTED_ENV__;
  if (!injected) {
    return null;
  }

  return PublicEnvSchema.parse(injected);
}

function readEnvValue(name: keyof PublicEnvSchema) {
  const viteEnv = (import.meta.env ?? {}) as Record<string, string | undefined>;
  const viteValue = viteEnv[`VITE_${name}`] ?? viteEnv[name];
  if (viteValue) {
    return viteValue;
  }

  if (typeof process !== "undefined") {
    return process.env[name];
  }

  return undefined;
}

function normalizePublicSiteUrl(value: string) {
  return value.replace(/\/+$/, "");
}

function emptyStringToUndefined(value: unknown) {
  return typeof value === "string" && value.trim() === "" ? undefined : value;
}
